import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Button,
  useTheme,
  useMediaQuery,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  DeleteSweep as ClearAllIcon,
  ArrowBack as BackIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { API_BASE_URL } from '../config/api';
import adminAuthService from '../services/adminAuthService';

// RTL Theme
const rtlTheme = createTheme({
  direction: 'rtl',
  typography: {
    fontFamily: 'Cairo, Roboto, sans-serif',
  },
});

const MissingProducts = () => {
  const [missingProducts, setMissingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Load missing products
  const loadMissingProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/missing_products.php`, {
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setMissingProducts(data.data || []);
      } else {
        setError(data.message || 'فشل في تحميل المنتجات المفقودة');
      }
    } catch (error) {
      console.error('Error loading missing products:', error);
      setError('حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMissingProducts();
  }, []);

  // Handle delete single product
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/missing_products.php`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: productId })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(data.message);
        setTimeout(() => setSuccess(''), 3000);
        loadMissingProducts();
      } else {
        setError(data.message || 'فشل في حذف المنتج');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      setError('حدث خطأ في حذف المنتج');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Handle clear all products
  const handleClearAll = async () => {
    setClearDialogOpen(false);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/missing_products.php`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'clear_all' })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(data.message);
        setTimeout(() => setSuccess(''), 3000);
        loadMissingProducts();
      } else {
        setError(data.message || 'فشل في مسح القائمة');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error clearing products:', error);
      setError('حدث خطأ في مسح القائمة');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // If it's just a filename (no path), prepend the uploads path
    if (!imagePath.includes('/')) {
      return `${API_BASE_URL}/api/uploads/products/${imagePath}`;
    }
    return `${API_BASE_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
  };

  // Mobile Card View
  const MobileCard = ({ product }) => (
    <Card sx={{ mb: 2, boxShadow: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        {product.image && (
          <CardMedia
            component="img"
            sx={{ width: 120, height: 120, objectFit: 'cover' }}
            image={getImageUrl(product.image)}
            alt={product.product_name}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <CardContent sx={{ flex: '1 0 auto', pb: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
              {product.product_name}
            </Typography>
            
            {product.sku && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>SKU:</strong> {product.sku}
              </Typography>
            )}
            
            {product.brand_name && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>العلامة التجارية:</strong> {product.brand_name}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={`الكمية: ${product.quantity}`} 
                color="error" 
                size="small"
              />
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {formatDate(product.added_at)}
            </Typography>
          </CardContent>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, pt: 0 }}>
            <IconButton 
              color="error" 
              size="small"
              onClick={() => handleDeleteProduct(product.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Card>
  );

  // Desktop Table View
  const DesktopTable = () => (
    <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell align="center"><strong>الصورة</strong></TableCell>
            <TableCell><strong>اسم المنتج</strong></TableCell>
            <TableCell><strong>SKU</strong></TableCell>
            <TableCell><strong>العلامة التجارية</strong></TableCell>
            <TableCell align="center"><strong>الكمية</strong></TableCell>
            <TableCell><strong>تاريخ الإضافة</strong></TableCell>
            <TableCell align="center"><strong>إجراءات</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {missingProducts.map((product) => (
            <TableRow key={product.id} hover>
              <TableCell align="center">
                {product.image ? (
                  <img 
                    src={getImageUrl(product.image)} 
                    alt={product.product_name}
                    style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                  />
                ) : (
                  <Box 
                    sx={{ 
                      width: 60, 
                      height: 60, 
                      backgroundColor: '#f0f0f0', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      borderRadius: 1
                    }}
                  >
                    <InventoryIcon color="disabled" />
                  </Box>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {product.product_name}
                </Typography>
              </TableCell>
              <TableCell>{product.sku || '-'}</TableCell>
              <TableCell>{product.brand_name || '-'}</TableCell>
              <TableCell align="center">
                <Chip label={product.quantity} color="error" size="small" />
              </TableCell>
              <TableCell>
                <Typography variant="caption">
                  {formatDate(product.added_at)}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <IconButton 
                  color="error" 
                  size="small"
                  onClick={() => handleDeleteProduct(product.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (!adminAuthService.isAuthenticated()) {
    navigate('/admin/login');
    return null;
  }

  return (
    <ThemeProvider theme={rtlTheme}>
      <div dir="rtl" style={{ direction: 'rtl', textAlign: 'right' }}>
        <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
          {/* App Bar */}
          <AppBar position="sticky" sx={{ backgroundColor: '#1976d2' }}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => navigate('/admin/packaging')}
                sx={{ mr: 2 }}
              >
                <BackIcon />
              </IconButton>
              
              <InventoryIcon sx={{ mr: 1 }} />
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                المنتجات المفقودة
              </Typography>
              
              <IconButton color="inherit" onClick={loadMissingProducts}>
                <RefreshIcon />
              </IconButton>
              
              {missingProducts.length > 0 && (
                <Button
                  color="inherit"
                  startIcon={<ClearAllIcon />}
                  onClick={() => setClearDialogOpen(true)}
                  sx={{ mr: 1 }}
                >
                  مسح الكل
                </Button>
              )}
            </Toolbar>
          </AppBar>

          {/* Content */}
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            {/* Alerts */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {/* Statistics */}
            <Paper sx={{ p: 2, mb: 3, textAlign: 'center' }}>
              <Typography variant="h4" color="error" sx={{ fontWeight: 'bold' }}>
                {missingProducts.length}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                منتج مفقود
              </Typography>
            </Paper>

            {/* Loading */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : missingProducts.length === 0 ? (
              <Paper sx={{ p: 5, textAlign: 'center' }}>
                <InventoryIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 1 }}>
                  لا توجد منتجات مفقودة
                </Typography>
                <Typography color="text.secondary">
                  جميع المنتجات متوفرة في المخزون
                </Typography>
              </Paper>
            ) : (
              <>
                {isMobile ? (
                  <Box>
                    {missingProducts.map((product) => (
                      <MobileCard key={product.id} product={product} />
                    ))}
                  </Box>
                ) : (
                  <DesktopTable />
                )}
              </>
            )}
          </Box>

          {/* Clear All Confirmation Dialog */}
          <Dialog
            open={clearDialogOpen}
            onClose={() => setClearDialogOpen(false)}
          >
            <DialogTitle>تأكيد الحذف</DialogTitle>
            <DialogContent>
              <Typography>
                هل أنت متأكد من حذف جميع المنتجات المفقودة؟ لا يمكن التراجع عن هذا الإجراء.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setClearDialogOpen(false)}>
                إلغاء
              </Button>
              <Button onClick={handleClearAll} color="error" variant="contained">
                حذف الكل
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </div>
    </ThemeProvider>
  );
};

export default MissingProducts;
